import React, { useState } from 'react';
import Styled from 'styled-components';
import { Box, Flex } from '@components/layout/Grid';
import Button from '@components/buttons';
import DocumentIcon from '@assets/DocumentIcon';
import Icon from '@components/Icon';
import { Trans } from '@lingui/react';
import Spinner from '@components/Spinner';
import { ProgramCategory } from '@gql/generated';

const Wrapper = Styled.div<{ category: ProgramCategory }>`
  min-height: 60vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 50px 20px;
  background-color: ${({ category }) =>
    category === ProgramCategory['Challenges'] ? '#D9E8E7' : '#e3f4fe'};

svg {
    height: 64px;
    width: 64px;

    path {
      fill: ${({ category, theme }) => {
        return theme.helpers.getProgramThemeColor(category);
      }};
    }
  }
`;

const Title = Styled.p`
  font-size: 20px;
  font-weight: 300;
  line-height: 17px;
  margin: 30px 0 20px 0;
`;

const DescriptionText = Styled.p`
  max-width: 650px;
  font-size: 14px;
  text-align: center;
  margin-top: 0;
  margin-bottom: 20px;
`;

const ReadButton = Styled(Button)`
  height: 44px;
`;

const Download = Styled.button`
  width: 160px;
  height: 44px;
  color: #1C343E;
  font-size: 13px;
  font-weight: 600;
  line-height: 16px;
  text-decoration: none;
  border: 1px solid;
  border-radius: 3px;
  padding: 11px 24px;
  background-color: transparent;
  cursor: pointer;
`;

const JoinProgram = Styled.p`
  font-size: 16px;
  color: #1C343E;

  i {
    margin-right: 6px;

    :before {
      color: ${({ theme }) => theme.colors.secondary['gray-2']};
    }
  }
`;

type Props = {
  title: string;
  description: string;
  documentURL: string;
  hasAccess: boolean;
  documentOpened(): void;
  programCategory: ProgramCategory;
};
const DocumentOverlay = (props: Props) => {
  const [documentLoading, setDocumentLoading] = useState(false);
  const { title, description, documentURL, hasAccess, documentOpened } = props;

  function downloadFile() {
    const filename = documentURL.split('/').reverse()[0];
    setDocumentLoading(true);
    fetch(documentURL)
      .then(r => r.blob())
      .then(blobURL => {
        // Create an invisible A element
        const a = document.createElement('a');
        a.style.display = 'none';
        document.body.appendChild(a);

        a.href = window.URL.createObjectURL(
          new Blob([blobURL], { type: 'application/pdf' })
        );

        // Use download attribute to set set desired file name
        a.setAttribute('download', filename);

        // Trigger the download by simulating click
        a.click();

        // Cleanup
        window.URL.revokeObjectURL(a.href);
        document.body.removeChild(a);
      })
      .finally(() => {
        setDocumentLoading(false);
      });
  }

  return (
    <>
      <Wrapper data-testtype="DocumentOverlay" category={props.programCategory}>
        <DocumentIcon height="64px" width="64px" />
        <Title data-testid="DocumentOverlay--Title">{title}</Title>
        <DescriptionText data-testid="DocumentOverlay--Description">
          {description}
        </DescriptionText>
        <Flex
          flexDirection={['column', 'column', 'row', 'row']}
          justifyContent="center"
          alignItems="center"
        >
          {hasAccess ? (
            <>
              <Box mr={[0, 0, 10]} mb={[10, 10, 0]}>
                <ReadButton
                  appearance={
                    props.programCategory === ProgramCategory['Challenges']
                      ? 'green'
                      : 'stone'
                  }
                  data-testid="OpenDocument--Button"
                  href="#"
                  target="_blank"
                  onClick={() => {
                    window.open(documentURL, '_blank');
                    documentOpened();
                  }}
                >
                  <Trans id="weeklyProgram.document.open_document" />
                </ReadButton>
              </Box>
              <Download
                onClick={function() {
                  documentOpened();
                  downloadFile();
                }}
                data-testid="DownloadDocument--Button"
              >
                {documentLoading ? (
                  <Spinner spinnerColor="#242424" size={20} />
                ) : (
                  <Trans id="metadata.download" />
                )}
              </Download>
            </>
          ) : (
            <JoinProgram>
              <Icon fontSize="16px" name="ygb-icon-infob" />
              <Trans id="metadata.getStarted" />
            </JoinProgram>
          )}
        </Flex>
      </Wrapper>
    </>
  );
};

export default DocumentOverlay;
